/**
 * The keypad. SPEC.md §10, §11, §16.4, §18.4, §19, §20.2, §36, §3.
 *
 * One number per player per hand, composed in full before anything is sent, and
 * then committed by whichever of two buttons says what the loss does: it goes to
 * whoever wins the hand, or it goes to everybody. Those are the only two things a
 * loss can do, which is why they are two buttons and not a mode somebody had to
 * choose earlier. §3
 *
 * Nobody types who won. The winner is whoever never touches this screen, so the
 * one thing the keypad has to be honest about is that a report may sit there
 * doing nothing until the rest of the table has reported too — hence the status
 * line, which is the difference between "waiting on Sam" and "my tap was
 * dropped". §3.1
 *
 * Digits live in a local draft, so "20 — no, 25" is two keystrokes and nothing
 * reaches the server: there is no ledger row to undo, because nothing happened.
 * Committing again REPLACES this player's report rather than adding to it, so a
 * mistake that *was* committed is still one commit to fix. §12.2
 *
 * Every semantic decision is imported and none is restated here: who may report
 * (canEnterAs → canPlayerEnterHand), what the number will do to their own score
 * (handEntryImpact — for a loss to everyone that is the whole fan-out, not what
 * they typed), and all of the copy (entryActionLabel, entryActionDetail,
 * handStatusCopy). §11, §60
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  MAX_TRANSFER_AMOUNT,
  MIN_TRANSFER_AMOUNT,
  entryActionDetail,
  entryActionLabel,
  formatSigned,
  handEntryImpact,
  handStatusCopy,
  payEveryoneConfirmation,
  truncateName,
  type EntryShape,
  type HandTone,
} from '@card-counter/shared';
import { Avatar } from '@/components/ui/Avatar';
import { Sheet } from '@/components/ui/Sheet';
import { Spinner } from '@/components/ui/Spinner';
import { useSession } from '@/hooks/useSession';
import {
  useActiveRound,
  useCanEnterAs,
  useDrinkingMode,
  useHandProgress,
  useHandEntryOf,
  useMyPlayerId,
  usePlayerById,
  usePointsPerDrink,
  useSessionStore,
} from '@/stores/session-store';
import {
  EMPTY_KEYPAD_DRAFT,
  useKeypadDraft,
  useUiStore,
  type KeypadDraft,
} from '@/stores/ui-store';

/** Widest legal amount, so a draft can never compose something the schema rejects. */
const MAX_DIGITS = String(MAX_TRANSFER_AMOUNT).length;

/**
 * Appends a digit to a draft.
 *
 * Leading zeros are dropped, so "0" then "7" is 7 and an empty draft stays empty
 * when 0 is the first key — a loss of zero is not a thing. Anything over the
 * shared maximum lands *on* the maximum rather than being rejected later, so a
 * fat-fingered extra 9 never turns into an error toast. §10.3
 */
export function appendDigit(digits: string, digit: string): string {
  const next = `${digits}${digit}`.replace(/^0+/, '');
  if (next === '') return '';
  if (next.length > MAX_DIGITS) return digits;
  return Number(next) > MAX_TRANSFER_AMOUNT ? String(MAX_TRANSFER_AMOUNT) : next;
}

/**
 * Types a digit into a draft.
 *
 * A pristine draft holds a number the player did not type — their standing
 * report, prefilled so they can see it. The first digit therefore *replaces* it
 * rather than appending, exactly as a selected input or a calculator behaves:
 * correcting a committed 57 to 9 has to give 9, not 579. §10.3
 */
export function typeDigit(current: KeypadDraft, digit: string): KeypadDraft {
  const base = current.pristine ? '' : current.digits;
  return { digits: appendDigit(base, digit), pristine: false };
}

/**
 * Deletes the last digit. This is the one edit that treats a prefilled value as
 * already-typed, because trimming 57 to 5 is a normal way to correct it.
 */
export function deleteDigit(current: KeypadDraft): KeypadDraft {
  return { digits: current.digits.slice(0, -1), pristine: false };
}

/** The magnitude a draft stands for. 0 means "nothing to commit". */
function amountOf(digits: string): number {
  return digits === '' ? 0 : Number(digits);
}

/** Tone for the hand status line. Colour reinforces the words, never alone. §19 */
const STATUS_TONE: Record<HandTone, string> = {
  idle: 'text-ink-faint',
  waiting: 'text-ink-muted',
  settled: 'text-positive',
  stuck: 'text-warn',
};

/**
 * Backspace, drawn rather than typed.
 *
 * The ⌫ glyph renders as a different weight and baseline on every platform and
 * as a tofu box on a few, which is a poor showing for the key a player hits most
 * after the digits. §19
 */
function BackspaceIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      className="size-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20.2 4.8H9.6a2 2 0 0 0-1.47.64l-4.9 5.2a2 2 0 0 0 0 2.72l4.9 5.2a2 2 0 0 0 1.47.64h10.6a1.8 1.8 0 0 0 1.8-1.8V6.6a1.8 1.8 0 0 0-1.8-1.8Z" />
      <path d="m17 9.5-5 5m0-5 5 5" />
    </svg>
  );
}

/**
 * The drink key's glyph: a tumbler with a measure in it. §4.6
 *
 * Drawn rather than set as an emoji for the same reason the backspace is: 🥃
 * renders as a different size, baseline and colour on every platform and as a
 * tofu box on a few, and this key is the one control on the screen that is
 * identified by its picture alone. The liquid is a filled path so the key still
 * reads as a drink in high contrast, where a stroked outline of it would not.
 */
function DrinkIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      className="size-7"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* The measure, filled: the glass reads as full rather than as an empty box. */}
      <path d="M6.1 13h11.8l-.55 6.1a2 2 0 0 1-2 1.9H8.65a2 2 0 0 1-2-1.9Z" fill="currentColor" stroke="none" />
      {/* The tumbler, very slightly tapered, as a rocks glass is. */}
      <path d="M5.5 5.4h13l-1.15 13.7a2 2 0 0 1-2 1.9H8.65a2 2 0 0 1-2-1.9Z" />
      <path d="M6.1 13h11.8" />
    </svg>
  );
}

/** Standby panel, used for every state where this player has no number to type. */
function Standby({ label, title, detail }: { label: string; title: string; detail: string }) {
  return (
    <section
      aria-label={label}
      role="status"
      className="flex min-h-0 flex-1 flex-col items-center justify-center gap-1.5 rounded-card border border-line bg-surface p-edge text-center"
    >
      <p className="text-lg font-semibold text-ink">{title}</p>
      <p className="max-w-xs text-sm text-ink-muted">{detail}</p>
    </section>
  );
}

interface KeyProps {
  label: string;
  disabled: boolean;
  onPress(): void;
  /** Clear and backspace read as controls, not as values. */
  muted?: boolean;
  /**
   * The drink key, which is the one key on this pad that is not about the
   * number being typed. It is the only red thing on the screen for exactly that
   * reason, and it never carries its meaning by colour alone — it has a glyph
   * nothing else on the pad has and a label a screen reader reads out. §19, §4.6
   */
  tone?: 'drink';
  children: React.ReactNode;
}

/**
 * One key. A real button with a real label, at least 48 px tall through
 * --keypad-key-height, and a pressed state that fires on the press rather than on
 * the network. §18.4, §19, §36
 */
function Key({ label, disabled, onPress, muted = false, tone, children }: KeyProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={label}
      onClick={onPress}
      className={[
        'grid min-h-key place-items-center rounded-control border text-2xl font-semibold select-none',
        'transition-[transform,border-color,background-color] duration-100 ease-out-soft',
        'active:scale-95',
        'disabled:pointer-events-none disabled:opacity-40',
        tone === 'drink'
          ? 'border-negative/60 bg-negative-soft text-negative active:border-negative active:bg-negative/25'
          : [
              'active:border-accent active:bg-accent-soft',
              muted ? 'border-line bg-surface text-ink-muted' : 'border-line bg-raised text-ink',
            ].join(' '),
      ].join(' ')}
    >
      {children}
    </button>
  );
}

/**
 * Three columns of four rows, which is the shape of every phone keypad and the
 * shape a thumb already knows. The grid is the flexible part of the screen: it
 * takes whatever the summary above and the actions below have not claimed, so on
 * a tall phone the keys are simply bigger. §18.2, §18.4
 */
const KEYS: readonly (string | 'clear' | 'backspace')[] = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  'clear',
  '0',
  'backspace',
];

interface ActionProps {
  shape: EntryShape;
  amount: number;
  participantCount: number;
  /** What the table is playing for, which changes what both shapes do. §4.6 */
  drinkingMode: boolean;
  disabled: boolean;
  replacing: boolean;
  pending: boolean;
  onPress(): void;
}

/**
 * One of the two things a loss can do, priced before it is committed.
 *
 * The figure on the right is what the player's own score will move by, from the
 * shared rule — 20 to everyone at a table of five is 80, and finding that out
 * afterwards is exactly the surprise this exists to prevent. §2.1
 */
function EntryAction({
  shape,
  amount,
  participantCount,
  drinkingMode,
  disabled,
  replacing,
  pending,
  onPress,
}: ActionProps) {
  const impact = handEntryImpact({ shape, amount, participantCount });
  const opponents = Math.max(1, participantCount - 1);
  const label = entryActionLabel(shape, amount);
  const detail = entryActionDetail({ shape, participantCount, drinkingMode });
  const toEveryone = shape === 'TO_EVERYONE';

  return (
    <button
      type="button"
      disabled={disabled}
      aria-busy={pending || undefined}
      // Named so the two can never be confused by anything reading the page:
      // "Report losing 20." and "Report losing 20 to everyone." §19
      aria-label={`${replacing ? 'Replace your report with' : 'Report losing'} ${String(amount)}${
        toEveryone ? ' to everyone' : ''
      }. ${detail}`}
      onClick={onPress}
      className={[
        'flex min-h-commit shrink-0 items-center gap-3 rounded-card border px-3 py-2 text-left',
        'transition-[transform,filter] duration-150 ease-out-soft active:scale-[0.98]',
        'disabled:pointer-events-none disabled:opacity-40',
        toEveryone
          ? 'border-negative/50 bg-negative-soft text-ink'
          : 'border-accent bg-accent text-accent-ink',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        data-numeric
        className={[
          'grid size-9 shrink-0 place-items-center rounded-control text-sm font-bold',
          toEveryone ? 'bg-negative/20 text-negative' : 'bg-accent-ink/15',
        ].join(' ')}
      >
        {toEveryone ? `×${String(opponents)}` : '−1'}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate font-semibold">{label}</span>
        <span
          className={`block truncate text-[0.7rem] leading-tight ${
            toEveryone ? 'text-ink-muted' : 'text-accent-ink/75'
          }`}
        >
          {detail}
        </span>
      </span>

      <span aria-hidden="true" className="shrink-0 text-right">
        <span data-numeric className="block text-base leading-none font-bold">
          {formatSigned(impact)}
        </span>
        <span
          className={`block text-[0.6rem] leading-tight ${
            toEveryone ? 'text-ink-faint' : 'text-accent-ink/70'
          }`}
        >
          total
        </span>
      </span>
    </button>
  );
}

export function AmountKeypad() {
  const round = useActiveRound();
  const myPlayerId = useMyPlayerId();
  const progress = useHandProgress();
  const players = useSessionStore((state) => state.players);

  const controllingPlayerId = useUiStore((state) => state.controllingPlayerId);
  const controlling = usePlayerById(controllingPlayerId);
  const setControllingPlayer = useUiStore((state) => state.setControllingPlayer);
  const overlayOpen = useUiStore(
    (state) =>
      state.hostMenuOpen ||
      state.historyOpen ||
      state.playersOpen ||
      state.avatarPickerOpen ||
      state.adminPlayerId !== null,
  );

  const draft = useKeypadDraft();
  // Bumped when the server refuses a report as contradicting the hand. §3.2
  const rejectedEntryCount = useUiStore((state) => state.rejectedEntryCount);
  const setKeypadDraft = useUiStore((state) => state.setKeypadDraft);
  const { interactive, setHandEntry, clearHandEntry, recordDrink } = useSession();
  const drinkingMode = useDrinkingMode();
  const pointsPerDrink = usePointsPerDrink();

  // The host may be reporting this for someone else; everything below then acts
  // as that player. §30
  const actorPlayerId = controllingPlayerId ?? myPlayerId;
  const entersThisHand = useCanEnterAs(actorPlayerId);
  /** The number this player already has standing for the hand, if any. §12.2 */
  const entry = useHandEntryOf(actorPlayerId);

  const [pending, setPending] = useState<EntryShape | null>(null);
  const [clearing, setClearing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  /** "Finished your drink?" — asked before any points move. §4.6, §51 */
  const [confirmingDrink, setConfirmingDrink] = useState(false);
  const [drinking, setDrinking] = useState(false);

  const amount = amountOf(draft.digits);
  const busy = pending !== null || clearing;
  const canCommit = amount >= MIN_TRANSFER_AMOUNT && interactive && !busy;

  /**
   * Prefills from the standing report so a player who already reported sees their
   * own number and can simply retype it. Keyed on the report itself rather than
   * on mount, which is what stops somebody else's report arriving mid-keystroke
   * from wiping a half-typed amount: only this player's own changing does that,
   * and then the draft *should* follow it.
   */
  const prefilled = useRef<string | null>(null);
  useEffect(() => {
    const key = `${round?.id ?? 'none'}:${actorPlayerId ?? 'none'}:${
      entry ? `${String(entry.amount)}:${entry.shape}` : 'empty'
    }`;
    if (prefilled.current === key) return;
    prefilled.current = key;
    setKeypadDraft(
      entry ? { digits: String(entry.amount), pristine: true } : EMPTY_KEYPAD_DRAFT,
    );
  }, [round?.id, actorPlayerId, entry, setKeypadDraft]);

  /**
   * Every edit is applied to the draft in the store rather than to the one this
   * render closed over. Several keys can land in a single React batch — a fast
   * thumb, a held key, a keyboard burst — and a stale closure would make 4 5 6
   * compose "6" instead of "456".
   */
  const editDraft = (edit: (current: KeypadDraft) => KeypadDraft): void => {
    setKeypadDraft(edit(useUiStore.getState().keypadDraft));
  };

  const commit = async (shape: EntryShape): Promise<void> => {
    const value = amountOf(useUiStore.getState().keypadDraft.digits);
    if (value < MIN_TRANSFER_AMOUNT || !interactive || busy) return;
    setPending(shape);
    // Optimistic only in the sense that the button responds at once. No total
    // moves until the server's broadcast lands — and for most reports no total
    // moves at all yet, which the status line says out loud. §36, §3.1
    await setHandEntry(value, shape);
    setPending(null);
    setConfirming(false);
  };

  /**
   * Hands the player their number back. §12.2
   *
   * It withdraws the standing report — which is the only undo the model has, and
   * takes every row that report had derived with it — and the keypad is then
   * empty for them to enter it again. "Resubmit" is what they came here to do;
   * withdrawing is only how it is done.
   */
  const resubmit = async (): Promise<void> => {
    setClearing(true);
    await clearHandEntry();
    setClearing(false);
  };

  /**
   * One finished drink. §4.6
   *
   * The keypad draft is deliberately untouched: drinking is not entering a
   * number, and a player halfway through typing 25 when the round arrives must
   * not lose it. Nothing is cheered from here either — the overlay is started by
   * the server's broadcast, so this device sees it at the same moment the rest
   * of the table does. §10, §15.3
   */
  const confirmDrink = async (): Promise<void> => {
    setDrinking(true);
    await recordDrink();
    setDrinking(false);
    setConfirmingDrink(false);
  };

  const showKeypad = round !== null && entersThisHand;

  /**
   * Hardware keyboard support, for a host running the table from a laptop.
   *
   * Bound to the document because the keypad has no text field to focus — there
   * is deliberately no <input>, so a stray tap can never raise the OS keyboard
   * over the table. Enter is left alone whenever a control has focus, so tabbing
   * to a key and pressing Enter presses that key rather than committing. §19
   *
   * Enter commits the ordinary loss and never the one that pays the whole table:
   * that one is worth several times the number on screen, so it keeps its
   * confirmation. §51
   */
  const onKeyDownRef = useRef<(event: KeyboardEvent) => void>(() => undefined);
  onKeyDownRef.current = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    const tag = target?.tagName ?? '';
    if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable === true) return;
    // A sheet is a modal surface; keystrokes belong to it, not to the table. §47
    if (document.querySelector('[role="dialog"]') !== null) return;

    if (/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      editDraft((current) => typeDigit(current, event.key));
      return;
    }
    if (event.key === 'Backspace') {
      event.preventDefault();
      editDraft(deleteDigit);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setKeypadDraft(EMPTY_KEYPAD_DRAFT);
      return;
    }
    if (event.key === 'Enter' && tag !== 'BUTTON' && tag !== 'A') {
      event.preventDefault();
      void commit('TO_WINNER');
    }
  };

  useEffect(() => {
    if (!showKeypad || overlayOpen || !interactive) return;
    const listener = (event: KeyboardEvent): void => {
      onKeyDownRef.current(event);
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [showKeypad, overlayOpen, interactive]);

  const winnerName = useMemo(() => {
    const winner = players.find((player) => player.id === progress.winnerPlayerId);
    return winner ? truncateName(winner.name) : null;
  }, [players, progress.winnerPlayerId]);

  // --- Standby states --------------------------------------------------------

  if (round === null) {
    return (
      <Standby
        label="Report your loss"
        title="No hand in progress"
        detail="Deal the next hand when the table is ready — anyone can."
      />
    );
  }

  if (!entersThisHand) {
    // The only way to be here is to have sat down after this hand opened. §3.3
    return (
      <Standby
        label="Report your loss"
        title="You joined mid-hand"
        detail="This hand started before you sat down. The next one is yours."
      />
    );
  }

  // --- The keypad ------------------------------------------------------------

  const participantCount = round.participantIds.length;
  const disabled = !interactive || busy;
  const status = handStatusCopy({
    reportedCount: progress.reportedCount,
    silentCount: progress.silentCount,
    needsWinner: progress.needsWinner,
    winnerName,
    drinkingMode,
  });
  // Composed once for the sheet below, which is where a loss to everyone is
  // spelled out before it is committed. §51, §4.6
  const payEveryone = payEveryoneConfirmation({ amount, participantCount, drinkingMode });
  const standingImpact = entry
    ? handEntryImpact({ shape: entry.shape, amount: entry.amount, participantCount })
    : null;

  const announcement = !interactive
    ? 'Reconnecting. The keypad is unavailable.'
    : amount === 0
      ? `No amount typed yet. ${status.title}.`
      : `${String(amount)} typed. ${
          standingImpact === null
            ? ''
            : `Replaces the ${formatSigned(standingImpact)} already reported. `
        }${status.title}.`;

  return (
    <section
      aria-label="Report your loss"
      className="entry-stack flex min-h-0 flex-1 flex-col"
    >
      {/* Acting for someone else is stated, never implied. §30 */}
      {controlling ? (
        <div className="flex min-h-tap shrink-0 items-center gap-2 rounded-control border border-accent bg-accent-soft px-3 text-sm">
          <Avatar avatarKey={controlling.avatarKey} className="size-7 shrink-0" />
          <span className="min-w-0 flex-1 truncate text-ink">
            Reporting for <strong className="font-semibold">{truncateName(controlling.name)}</strong>
          </span>
          <button
            type="button"
            onClick={() => {
              setControllingPlayer(null);
            }}
            className="shrink-0 rounded-control px-2 py-1 font-semibold tracking-wide text-accent uppercase"
          >
            Done
          </button>
        </div>
      ) : null}

      {/* The draft, and what it would replace. One row, because a row that only
          appeared once a player had reported would push the keys down at the
          exact moment they are being used. §18.2

          Keyed on the refusal counter so a refused report flashes it red every
          time, including twice in a row: a CSS animation whose name has not
          changed does not restart, so re-tapping a refused action would show
          nothing and read as a dropped tap. The key changes, the element is
          fresh, the animation plays. Nothing about the box's size or position
          depends on it, so the keypad below never moves. §3.2, §19, §18.2 */}
      <div
        key={`draft-${String(rejectedEntryCount)}`}
        data-rejected={rejectedEntryCount > 0 ? '' : undefined}
        className="min-h-draft entry-refused flex shrink-0 items-center gap-2 rounded-card border border-line bg-surface pr-1 pl-3">
        <div aria-hidden="true" className="flex min-w-0 flex-1 flex-col">
          <p className={`truncate text-xs leading-tight ${STATUS_TONE[status.tone]}`}>
            {!interactive ? 'Reconnecting…' : status.title}
          </p>
          <p className="truncate text-[0.65rem] leading-tight text-ink-faint">
            {/* What replaces what is normally the more useful of the two, but a
                stuck hand is the one state that needs an instruction rather than
                a description — and the player holding a report is exactly who can
                act on it. §3.1 */}
            {standingImpact === null || status.tone === 'stuck'
              ? status.detail
              : `Replaces ${formatSigned(standingImpact)} reported`}
          </p>
        </div>

        {!interactive ? (
          <Spinner size="sm" label="Reconnecting" />
        ) : (
          <p aria-hidden="true" data-numeric className="text-draft font-bold text-ink">
            {amount === 0 ? <span className="text-ink-faint">0</span> : amount}
          </p>
        )}

        {/* The only undo there is, offered only once there is something to take
            back — and named for the thing the player actually wants out of it,
            which is their number back on the keys. §12.2, §12.3 */}
        {entry ? (
          <button
            type="button"
            disabled={disabled}
            aria-label={`Resubmit: clears the ${formatSigned(standingImpact ?? 0)} already reported`}
            onClick={() => {
              void resubmit();
            }}
            className="min-h-tap shrink-0 rounded-control px-2 text-xs font-semibold tracking-wide text-accent uppercase disabled:opacity-45"
          >
            {clearing ? 'Clearing…' : 'Resubmit'}
          </button>
        ) : null}
      </div>

      {/* The draft is announced from here rather than from the digits, so a
          keystroke reads as "23 typed, two players still to report". §19 */}
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>

      {/* The flexible part of the screen, and the largest: this is what the
          player is holding the phone to do. §18.2, §20.2 */}
      {/* `keypad-keys` carries the floor: the grid grows into whatever the fixed
          rows leave, and never shrinks past the height four rows of 48 px keys
          need. Without it a short viewport collapses the grid and the rows draw
          on top of each other. §18.4 */}
      <div
        role="group"
        aria-label="Amount keypad"
        className="keypad-keys entry-stack grid flex-1 grid-cols-3 grid-rows-4"
      >
        {KEYS.map((key) => {
          if (key === 'backspace') {
            return (
              <Key
                key={key}
                muted
                label="Delete last digit"
                disabled={disabled || draft.digits === ''}
                onPress={() => {
                  editDraft(deleteDigit);
                }}
              >
                <BackspaceIcon />
              </Key>
            );
          }
          if (key === 'clear') {
            /**
             * In drinking mode this slot is the drink key instead. §4.6
             *
             * It takes C's place rather than a thirteenth key because the pad is
             * three columns of four and a fifth row would come out of the keys'
             * own height, which is already at its 48 px floor on a short
             * phone. Backspace is what is left for correcting a draft, which is
             * how a draft is usually corrected anyway — and Escape still clears
             * it outright on a keyboard. §18.2, §18.4
             */
            if (drinkingMode) {
              return (
                <Key
                  key={key}
                  tone="drink"
                  label={
                    pointsPerDrink === null
                      ? 'Finished a drink'
                      : `Finished a drink, worth ${String(pointsPerDrink)} points`
                  }
                  disabled={!interactive || drinking}
                  onPress={() => {
                    setConfirmingDrink(true);
                  }}
                >
                  <DrinkIcon />
                </Key>
              );
            }
            return (
              <Key
                key={key}
                muted
                label="Clear the amount"
                disabled={disabled || draft.digits === ''}
                onPress={() => {
                  setKeypadDraft(EMPTY_KEYPAD_DRAFT);
                }}
              >
                <span aria-hidden="true" className="text-lg">
                  C
                </span>
              </Key>
            );
          }
          return (
            <Key
              key={key}
              label={key}
              disabled={disabled}
              onPress={() => {
                editDraft((current) => typeDigit(current, key));
              }}
            >
              <span aria-hidden="true">{key}</span>
            </Key>
          );
        })}
      </div>

      {/* The only two controls on this screen that send anything. §20.2 */}
      <div className="entry-stack flex shrink-0 flex-col">
        <EntryAction
          shape="TO_WINNER"
          amount={amount}
          participantCount={participantCount}
          drinkingMode={drinkingMode}
          disabled={!canCommit}
          replacing={entry !== null}
          pending={pending === 'TO_WINNER'}
          onPress={() => {
            void commit('TO_WINNER');
          }}
        />
        <EntryAction
          shape="TO_EVERYONE"
          amount={amount}
          participantCount={participantCount}
          drinkingMode={drinkingMode}
          disabled={!canCommit}
          replacing={entry !== null}
          pending={pending === 'TO_EVERYONE'}
          onPress={() => {
            setConfirming(true);
          }}
        />
      </div>

      {/*
        §51 — the one commit worth confirming. It moves several times the number
        on screen, and unlike an ordinary loss it lands on the whole table at
        once, so the figure is spelled out before it does.

        Every word of it comes from `payEveryoneConfirmation`, because what the
        shape *does* is not the same at both kinds of table and this is the screen
        whose whole job is to say so. Written out here it said "each of the other
        3 players receives 20" while a drinking table destroyed the sixty and
        credited nobody. §4.6, §60
      */}
      <Sheet
        open={confirming}
        onClose={() => {
          setConfirming(false);
        }}
        title={payEveryone.title}
        description={payEveryone.description}
        footer={
          <div className="grid grid-cols-[1fr_1.2fr] gap-2">
            <button
              type="button"
              onClick={() => {
                setConfirming(false);
              }}
              className="min-h-tap rounded-control border border-line bg-surface font-semibold text-ink"
            >
              Cancel
            </button>
            <button
              type="button"
              data-autofocus
              disabled={!canCommit}
              onClick={() => {
                void commit('TO_EVERYONE');
              }}
              className="min-h-tap rounded-control border border-negative/50 bg-negative-soft font-semibold text-negative disabled:opacity-45"
            >
              {payEveryone.confirm}
            </button>
          </div>
        }
      >
        <div className="rounded-card border border-negative/40 bg-negative-soft px-4 py-3">
          <p data-numeric className="text-2xl font-bold text-negative">
            {formatSigned(
              handEntryImpact({ shape: 'TO_EVERYONE', amount, participantCount }),
            )}
          </p>
          <p className="text-sm text-ink-muted">{payEveryone.working}</p>
        </div>
      </Sheet>

      {/*
        §4.6 — the drink. Confirmed rather than credited on the first tap for the
        same reason paying the whole table is: it moves points, it is the one
        control on this pad that does so without a number being typed, and it
        sits where C used to — which is a key a thumb reaches for out of habit.
      */}
      <Sheet
        open={confirmingDrink}
        onClose={() => {
          setConfirmingDrink(false);
        }}
        title={controlling ? 'Finished their drink?' : 'Finished your drink?'}
        description={
          controlling
            ? `${truncateName(controlling.name)} comes back towards level.`
            : 'Points come back towards level.'
        }
        footer={
          <div className="grid grid-cols-[1fr_1.2fr] gap-2">
            <button
              type="button"
              onClick={() => {
                setConfirmingDrink(false);
              }}
              className="min-h-tap rounded-control border border-line bg-surface font-semibold text-ink"
            >
              Not yet
            </button>
            <button
              type="button"
              data-autofocus
              disabled={!interactive || drinking}
              onClick={() => {
                void confirmDrink();
              }}
              className="min-h-tap rounded-control border border-positive/50 bg-positive-soft font-semibold text-positive disabled:opacity-45"
            >
              {drinking ? 'Recording…' : 'Yes, finished'}
            </button>
          </div>
        }
      >
        <div className="flex items-center gap-3 rounded-card border border-positive/40 bg-positive-soft px-4 py-3">
          <span aria-hidden="true" className="text-positive">
            <DrinkIcon />
          </span>
          <div className="min-w-0">
            {/* The figure is the session's, never this device's: what a cup is
                worth was agreed when the table was created. §4.6, §15.1 */}
            <p data-numeric className="text-2xl font-bold text-positive">
              {pointsPerDrink === null ? '—' : formatSigned(pointsPerDrink)}
            </p>
            {/* Whose score, said the same way the description above says it —
                the host recording a drink for somebody who put their phone down
                sends `actorPlayerId`, and the points land on them. Two lines of
                one sheet disagreeing about who is being credited is worse than
                either line alone. §30, §4.6 */}
            <p className="text-sm text-ink-muted">
              {controlling
                ? `Added to ${truncateName(controlling.name)}'s score.`
                : 'Added to your score.'}
            </p>
          </div>
        </div>
      </Sheet>
    </section>
  );
}

